import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mountains, ShieldCheck } from "@phosphor-icons/react";
import Logo from "../components/Logo";

const HERO = "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=2000";

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const nav = useNavigate();

  if (user) {
    return <Navigate to={user.role === "super_admin" ? "/admin/clientes" : "/dashboard"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    try {
      const u = await login(email, password);
      toast.success("Bienvenido a SIGSAOP");
      nav(u.role === "super_admin" ? "/admin/clientes" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar sesión");
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left visual */}
      <div className="hidden lg:flex w-1/2 relative items-end p-14 text-white"
        style={{ backgroundImage: `linear-gradient(180deg, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.85) 100%), url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div>
          <div className="flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.18em] text-slate-300">
            <Mountains size={16} weight="fill" /> Plataforma corporativa
          </div>
          <h2 className="font-display text-5xl font-bold leading-tight max-w-md">
            Gestión territorial, social y ambiental en un solo lugar.
          </h2>
          <p className="mt-6 max-w-lg text-slate-300 leading-relaxed">
            El <strong className="text-white font-semibold">Sistema de Gestión Social y Ambiental para Operaciones y Proyectos (SIGSAOP)</strong> centraliza el trabajo con comunidades, autoridades y tus distintos grupos de interés, haciendo registro, control y seguimiento a reuniones, acuerdos, compromisos voluntarios o de RCA, inversión social, entre otros.
          </p>
          <p className="mt-4 max-w-lg text-slate-300 leading-relaxed">
            <strong className="text-white font-semibold">Certificaciones nacionales e internacionales:</strong> en un solo lugar está la trazabilidad y reportabilidad de toda tu gestión.
          </p>
          <div className="mt-10 flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck size={14} /> Datos cifrados · Trazabilidad completa
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="flex items-center">
              <img src="/assets/sigsaop-logo.svg?v=5" alt="SIGSAOP" className="h-36 w-auto object-contain" data-testid="login-logo" />
            </div>
          </div>
          <div className="space-y-1.5">
            <h1 className="font-display text-2xl font-semibold">Iniciar sesión</h1>
            <p className="text-sm text-slate-600">
              Ingresa con tu cuenta corporativa para acceder al sistema.
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-700">Email</Label>
              <Input id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-700">Contraseña</Label>
              <Input id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" autoComplete="current-password" />
            </div>
          </div>
          <Button type="submit" disabled={loading}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="login-submit-button">
            {loading ? "Ingresando…" : "Ingresar"}
          </Button>
        </form>
      </div>
    </div>
  );
}
